<!--
  - Copyright (c) 2023-2024. Haodong JU
  -->

<template>
  <div>
    <div class="d-flex">
      <v-tabs
        v-model="tab"
        background-color="transparent"
        color="black"
        style="background: transparent"
      >
        <v-tab>{{ $t('storeSubscription') }}</v-tab>
        <v-tab>{{ $t('SaveRights') }}</v-tab>
        <v-tab>{{ $t('allBills') }}</v-tab>
      </v-tabs>
      <v-spacer />
      <v-btn
        class="primary mr-2"
        @click="showPersonalBills"
      >
        {{ $t('SubscriptionOverview') }}
      </v-btn>
    </div>
    <v-divider />
    <v-tabs-items
      v-model="tab"
      class="mt-4"
      style="background: transparent"
    >
      <loading-provider :loading="loading">
        <v-tab-item>
          <v-card
            v-for="s in subscriptionsForStore"
            :key="s.id"
            class="pa-2 mb-2"
            elevation="0"
          >
            <div class="d-flex align-center justify-center">
              <div class="font-weight-bold text-body-1">
                {{ s.priceName }}
              </div>
              <v-spacer />
              <div>
                <v-btn
                  icon
                  @click="cancelSubscription(s)"
                >
                  <v-icon>mdi-cancel</v-icon>
                </v-btn>
              </div>
            </div>

            <div class="d-flex">
              <div>
                {{ translateStatus(s.status) }}/{{ $t('createIn') }}{{ timestampToDisplay(s.subscriptionStartDate) }}
              </div>
              <v-spacer />
              <div>{{ s.subscriptionId }}</div>
            </div>
            <div class="d-flex align-center justify-center">
              <div>
                {{ $t('endOfThisPeriod') }}: {{ timestampToDisplay(s.subscriptionEndDate) }}
              </div>
              <v-spacer />
            </div>
          </v-card>
        </v-tab-item>
        <v-tab-item>
          <div style="display: grid;grid-template-columns: repeat(auto-fill,minmax(144px,1fr));grid-gap: 24px">
            <license-card
              v-for=" i in ownedProductForStore"
              :key="i.id"
              :license="i"
              :status="'active'"
            />
          </div>
        </v-tab-item>
        <v-tab-item>
          <v-card
            v-for="(item,index) in storeBills"
            :key="index"
            class="pa-2 mb-2"
            elevation="0"
          >
            <div class="d-flex align-center justify-center">
              <div class="font-weight-bold text-body-1">
                {{ item.account_name }}
              </div>
              <v-spacer />
              <v-btn
                icon
                @click="downloadInvoice(item)"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </div>
            <div class="d-flex align-center justify-center">
              <div>
                {{ item.customer_name }}
              </div>
              <v-spacer />
              <div> {{ item.customer_email }}</div>
            </div>
            <div class="d-flex">
              <div>
                {{ unixTimestampToDisplay(item.period_start) }}
              </div>
              <v-spacer />
              <div>{{ unixTimestampToDisplay(item.period_end) }}</div>
            </div>
            <div class="d-flex align-center justify-center">
              <div>
                {{ item.id }}
              </div>
              <v-spacer />
              <div> {{ item.number }}</div>
            </div>
          </v-card>
        </v-tab-item>
      </loading-provider>
    </v-tabs-items>
  </div>
</template>

<script>
import { loadProducts, ProductCodes } from '@/model/dataLayer/api/SaaS/product'
import { getActiveProductCode } from '@/model/dataLayer/api/SaaS/user'
import i18n from '@/i18n'
import { keyBy } from 'lodash-es'
import dayjs from 'dayjs'
import LicenseCard from '@/views/SaaS/personalInfo/LicenseCard.vue'
import { uniqBy } from 'lodash'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { FireBaseApp } from '@/model/firebase/takewayCompat'
import {
  cancelSubscription,
  getDeviceBills,
  getDeviceSubscriptionList,
  getUserSubscriptionList
} from '@/model/dataLayer/api/SaaS/serviceUsageService'
import LoadingProvider from '@/views/widget/LoadingProvider.vue'

export default {
  name: 'PersonalInfoPage',
  components: {
    LoadingProvider,
    LicenseCard
  },
  data () {
    return {
      storeBills: [],
      checkBillLoading: false,
      loading: false,
      ownedProductForStore: [],
      subscriptionsForStore: [],
      tab: null,
      products: []
    }
  },

  async mounted () {
    await this.reload()
  },
  methods: {
    downloadInvoice (item) {
      window.open(item.invoicePdf)
    },
    async showPersonalBills () {
      const res = await getUserSubscriptionList()
      window.open(res)
    },
    async cancelSubscription (item) {
      this.loading = true
      await cancelSubscription(item.subscriptionId)
      this.loading = false
    },
    timestampToDisplay (date) {
      return dayjs(date).format('YYYY-MM-DD HH:mm:ss')
    },
    unixTimestampToDisplay (date) {
      return dayjs.unix(date).format('YYYY-MM-DD HH:mm:ss')
    },
    translateStatus (status) {
      if (status === 'active') {
        return i18n.t('isActive')
      }
      if (status === 'canceled') {
        return i18n.t('Canceled')
      }
      if (status === 'incomplete') {
        return i18n.t('Incomplete')
      }
      if (status === 'incomplete_expired') {
        return i18n.t('IncompleteExpired')
      }
      if (status === 'past_due') {
        return i18n.t('PastDue')
      }
      if (status === 'trialing') {
        return i18n.t('Trialing')
      }
      if (status === 'unpaid') {
        return i18n.t('Unpaid')
      } else {
        return status
      }
    },
    dayjs,
    async reload () {
      this.loading = true
      this.products = keyBy(await loadProducts(), 'metadata.code')
      console.log(this.products, 'products')
      this.ownedProductForStore = uniqBy((await getActiveProductCode()).map(it => this.products[it])
        .filter(it => it?.metadata?.code !== ProductCodes.openDevice), 'id')
      try {
        this.subscriptionsForStore = await getDeviceSubscriptionList()
        this.storeBills = await getDeviceBills()
        console.log(this.storeBills, 'bills')
      } catch (e) {
        console.log(e, 'e')
      }

      this.loading = false
    },
    async viewOwnerBills () {
      this.checkBillLoading = true
      const functions = getFunctions(FireBaseApp)
      const functionRef = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink')
      const { data } = await functionRef({
        returnUrl: window.location.origin + window.location.pathname
      })
      window.location.assign(data.url)
      this.checkBillLoading = false
    }
  }
}
</script>

<style scoped>
.text-slate-500 {
  --tw-text-opacity: 1;
  color: rgb(100 116 139 / var(--tw-text-opacity));
}

.text-slate-600 {
  --tw-text-opacity: 1;
  color: rgb(71 85 105 / var(--tw-text-opacity));
}
</style>
